import type {
  IAdminCurrentUser,
  IAdminFieldPermissions,
  IAdminSectionPermissions,
} from '~/admin/shared/api/client/profiles/dto';

export type AdminSectionPermissionKey = keyof IAdminSectionPermissions;
export type AdminFieldPermissionKey = keyof IAdminFieldPermissions;

const NEWS_SECTION_KEYS: AdminSectionPermissionKey[] = [
  'materials',
  'experts',
  'publishers_news',
];

const EVENTS_SECTION_KEYS: AdminSectionPermissionKey[] = [
  'events',
  'employees',
  'partners',
  'speakers',
  'partner_types',
  'employee_roles',
  'program_items',
  'publishers_events',
];

const JOURNALS_SECTION_KEYS: AdminSectionPermissionKey[] = [
  'journals',
  'journal_editions',
  'journal_orders',
  'publishers_journals',
];

const PUBLISHER_ROUTE_NAMES = [
  'admin-chronicle',
  'admin-news-feed',
  'admin-companies-feed',
  'admin-main-news',
  'admin-video',
  'admin-offices',
  'admin-retail',
  'admin-afisha-events',
  'admin-stories',
  'admin-special',
  'admin-plus',
  'admin-day-number',
  'admin-experts',
  'admin-photos-galleries',
] as const;

const EVENTS_PUBLISHER_ROUTE_NAMES = [
  'admin-events-main',
  'admin-events-affiche',
  'admin-events-partners',
] as const;

const JOURNALS_PUBLISHER_ROUTE_NAMES = [
  'admin-journal-recommended',
  'admin-journal-advertising',
] as const;

type AdminRouteName = string | null | undefined;

export const canAccessAdminSection = (
  user: IAdminCurrentUser | null | undefined,
  section: AdminSectionPermissionKey,
) => {
  if (!user) {
    return false;
  }

  if (user.permissions.is_superuser) {
    return true;
  }

  return Boolean(user.permissions.sections[section]);
};

export const canUseAdminFieldPermission = (
  user: IAdminCurrentUser | null | undefined,
  field: AdminFieldPermissionKey,
) => {
  if (!user) {
    return false;
  }

  if (user.permissions.is_superuser) {
    return true;
  }

  return user.permissions.field_permissions[field];
};

export const hasAnyNewsSectionAccess = (
  user: IAdminCurrentUser | null | undefined,
) => NEWS_SECTION_KEYS.some((key) => canAccessAdminSection(user, key));

export const hasAnyEventsSectionAccess = (
  user: IAdminCurrentUser | null | undefined,
) => EVENTS_SECTION_KEYS.some((key) => canAccessAdminSection(user, key));

export const hasAnyJournalsSectionAccess = (
  user: IAdminCurrentUser | null | undefined,
) => JOURNALS_SECTION_KEYS.some((key) => canAccessAdminSection(user, key));

export const canAccessAdminRoute = (
  user: IAdminCurrentUser | null | undefined,
  routeName: AdminRouteName,
  routeParams?: Record<string, unknown>,
) => {
  if (!user || !routeName) {
    return false;
  }

  if (routeName === 'admin-login') {
    return true;
  }

  if (routeName === 'admin-users') {
    return canAccessAdminSection(user, 'users');
  }

  if (routeName === 'admin-users-edit') {
    if (canAccessAdminSection(user, 'users')) {
      return true;
    }

    return String(routeParams?.id ?? '') === String(user.id);
  }

  if (
    [
      'admin-news',
      'admin-news-edit',
      'admin-news-history',
      'admin-news-history-item',
    ].includes(routeName)
  ) {
    return (
      canAccessAdminSection(user, 'materials') ||
      canAccessAdminSection(user, 'experts')
    );
  }

  if (['admin-mailing', 'admin-mailing-edit'].includes(routeName)) {
    return canAccessAdminSection(user, 'mailing');
  }

  if (['admin-photos', 'admin-photo-edit'].includes(routeName)) {
    return canAccessAdminSection(user, 'photos');
  }

  if (['admin-photo-gallery', 'admin-photo-gallery-edit'].includes(routeName)) {
    return canAccessAdminSection(user, 'galleries');
  }

  if (['admin-videos', 'admin-video-edit'].includes(routeName)) {
    return canAccessAdminSection(user, 'videos');
  }

  if (['admin-video-gallery', 'admin-video-gallery-edit'].includes(routeName)) {
    return canAccessAdminSection(user, 'video_galleries');
  }

  if (routeName === 'admin-feed-special') {
    return canUseAdminFieldPermission(user, 'can_edit_special_project_feed');
  }

  if (
    PUBLISHER_ROUTE_NAMES.includes(
      routeName as (typeof PUBLISHER_ROUTE_NAMES)[number],
    )
  ) {
    return canAccessAdminSection(user, 'publishers_news');
  }

  if (
    EVENTS_PUBLISHER_ROUTE_NAMES.includes(
      routeName as (typeof EVENTS_PUBLISHER_ROUTE_NAMES)[number],
    )
  ) {
    return canAccessAdminSection(user, 'publishers_events');
  }

  if (routeName === 'admin-objects-persons') {
    return canAccessAdminSection(user, 'persons');
  }

  if (routeName === 'admin-objects-companies') {
    return canAccessAdminSection(user, 'companies');
  }

  if (routeName === 'admin-events-companies') {
    return canAccessAdminSection(user, 'companies');
  }

  if (routeName === 'admin-objects-authors') {
    return canAccessAdminSection(user, 'authors');
  }

  if (routeName === 'admin-objects-projects') {
    return canAccessAdminSection(user, 'projects');
  }

  if (routeName === 'admin-main-tags') {
    return canAccessAdminSection(user, 'categories');
  }

  if (routeName === 'admin-news-tags') {
    return canAccessAdminSection(user, 'sectors');
  }

  if (['admin-ai-tags', 'admin-ai-tags-edit'].includes(routeName)) {
    return canAccessAdminSection(user, 'ai_tags');
  }

  if (routeName === 'admin-site-settings') {
    return canAccessAdminSection(user, 'settings');
  }

  if (['admin-events', 'admin-events-edit'].includes(routeName)) {
    return canAccessAdminSection(user, 'events');
  }

  if (routeName === 'admin-speakers') {
    return canAccessAdminSection(user, 'speakers');
  }

  if (routeName === 'admin-employees') {
    return canAccessAdminSection(user, 'employees');
  }

  if (routeName === 'admin-partners') {
    return canAccessAdminSection(user, 'partners');
  }

  if (routeName === 'admin-partner-types') {
    return canAccessAdminSection(user, 'partner_types');
  }

  if (routeName === 'admin-employee-roles') {
    return canAccessAdminSection(user, 'employee_roles');
  }

  if (['admin-journals', 'admin-journals-edit'].includes(routeName)) {
    return canAccessAdminSection(user, 'journals');
  }

  if (routeName === 'admin-journal-editions') {
    return canAccessAdminSection(user, 'journal_editions');
  }

  if (routeName === 'admin-journal-orders') {
    return canAccessAdminSection(user, 'journal_orders');
  }

  if (
    JOURNALS_PUBLISHER_ROUTE_NAMES.includes(
      routeName as (typeof JOURNALS_PUBLISHER_ROUTE_NAMES)[number],
    )
  ) {
    return canAccessAdminSection(user, 'publishers_journals');
  }

  return true;
};

export const getDefaultAdminRouteName = (
  user: IAdminCurrentUser | null | undefined,
) => {
  if (!user) {
    return 'admin-login';
  }

  const orderedRoutes: AdminRouteName[] = [
    'admin-users',
    'admin-news',
    'admin-chronicle',
    'admin-events',
    'admin-events-main',
    'admin-speakers',
    'admin-employees',
    'admin-partners',
    'admin-partner-types',
    'admin-employee-roles',
    'admin-photos',
    'admin-photo-gallery',
    'admin-videos',
    'admin-video-gallery',
    'admin-objects-persons',
    'admin-objects-companies',
    'admin-objects-projects',
    'admin-objects-authors',
    'admin-main-tags',
    'admin-news-tags',
    'admin-journals',
  ];

  return (
    orderedRoutes.find((routeName) =>
      canAccessAdminRoute(user, routeName, { id: user.id }),
    ) || 'admin-login'
  );
};
